// What a theme may say, checked the same way wherever one comes from: an
// agent's tool call, the board's own form, or a link someone shared. Unknown
// keys are dropped, colours must be real colours, numbers are kept in range,
// lists are kept short. What survives is plain data the board draws itself.

import { cleanEffects, effectsLeftOut } from "./effects";
import { IMAGE_KEY } from "./imageInfo";
import { libraryOf } from "./library";
import type { CustomTheme, FontVoice, ParticleKind, SceneLayer, SceneLayerKind, SceneSpec, ThemeBackground, ThemeColors } from "./types";

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
export const LAYER_KINDS: SceneLayerKind[] = ["mountains", "peak", "hills", "dunes", "city", "village", "lighthouse", "forest", "plain", "cliffs"];
export const PARTICLE_KINDS: ParticleKind[] = ["fireflies", "snow", "rain", "petals", "embers", "leaves", "dust", "bubbles"];
const SCENE_KEYS = new Set(["style", "seed", "sky", "horizon", "body", "stars", "aurora", "clouds", "layers", "water", "grid", "particles", "flowers", "fog", "blobs", "grain", "vignette"]);
const FONTS: FontVoice[] = ["clean", "editorial", "pixel", "mono"];

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === "object" && !Array.isArray(v);

function hexOf(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim().toLowerCase();
  if (!HEX.test(s)) return undefined;
  return s.length === 4 ? `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}` : s;
}

function numOf(v: unknown, lo: number, hi: number): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? Math.max(lo, Math.min(hi, Math.round(v * 1000) / 1000)) : undefined;
}

function textOf(v: unknown, max: number): string {
  return typeof v === "string" ? v.replace(/[\u0000-\u001f\u007f<>]/g, "").replace(/\s+/g, " ").trim().slice(0, max) : "";
}

function colorsOf(v: unknown, max: number): string[] {
  return Array.isArray(v) ? v.map(hexOf).filter((c): c is string => !!c).slice(0, max) : [];
}

/**
 * What a theme asked for that the board cannot draw, in words, so whoever made
 * it can choose again instead of finding a bare cliff where a lighthouse was
 * meant to be. cleanScene drops all of it quietly.
 */
export function leftOut(raw: unknown): string[] {
  const t = isObj(raw) ? raw : {};
  const s = isObj(t.scene) ? t.scene : {};
  const out: string[] = [];
  const said = (v: unknown) => JSON.stringify(String(v ?? ""));
  for (const k of Object.keys(s)) if (!SCENE_KEYS.has(k)) out.push(`scene.${k}, which is not a part of a scene`);
  if (Array.isArray(s.layers))
    s.layers.forEach((l, i) => {
      if (i === 7) out.push("the layers after the seventh");
      if (i >= 7) return;
      if (!isObj(l)) out.push(`layer ${i + 1}, which is not an object`);
      else if (!LAYER_KINDS.includes(l.kind as SceneLayerKind)) out.push(`the ${said(l.kind)} layer, since the kinds are ${LAYER_KINDS.join(", ")}`);
      else if (!hexOf(l.color)) out.push(`the ${l.kind} layer, whose color must be #hex`);
    });
  if (isObj(s.body) && s.body.kind !== undefined && !["moon", "sun", "planet", "none"].includes(String(s.body.kind))) out.push(`a body of kind ${said(s.body.kind)}, since it is a moon, sun, planet or none`);
  if (isObj(s.particles) && !PARTICLE_KINDS.includes(s.particles.kind as ParticleKind)) out.push(`particles of ${said(s.particles.kind)}, since the kinds are ${PARTICLE_KINDS.join(", ")}`);
  for (const k of ["clouds", "water", "fog", "grid"]) if (isObj(s[k]) && !hexOf((s[k] as Obj).color)) out.push(`${k}, whose color must be #hex`);
  if (isObj(s.aurora) && !colorsOf(s.aurora.colors, 4).length) out.push("the aurora, which needs colors as #hex");
  if (Array.isArray(s.sky) && s.sky.some((c) => !hexOf(c))) out.push("sky colors that are not #hex");
  if (t.font !== undefined && !FONTS.includes(t.font as FontVoice)) out.push(`the font ${said(t.font)}, since it is clean, editorial, pixel or mono`);
  out.push(...effectsLeftOut(t.effects));
  if (t.drawing !== undefined && t.drawing !== null && !cleanDrawing(t.drawing)) out.push(`the drawing, which must be one SVG (from <svg to </svg>) under ${MAX_DRAWING.toLocaleString("en-US")} characters`);
  return out;
}

/**
 * The longest drawing a theme keeps: a detailed SVG landscape is a few tens of
 * thousands of characters. Counted by drawingLength.
 */
export const MAX_DRAWING = 150_000;

// what a browser needs an SVG to say before it shows it as a picture; cleanDrawing adds either one left out
const SVG_NS = ' xmlns="http://www.w3.org/2000/svg"';
const XLINK_NS = ' xmlns:xlink="http://www.w3.org/1999/xlink"';

/**
 * A drawing's length as the board counts it: without the two namespaces
 * cleanDrawing adds to one that leaves them out. So a drawing is as long kept
 * as when it came, and one the board took it takes again: the browser sends it
 * back with the picture it made of it, and is never turned away for what the
 * board itself added.
 */
export function drawingLength(svg: string): number {
  const root = /^\s*<svg\b[^>]*>/i.exec(svg)?.[0] ?? "";
  return svg.length - (root.includes(SVG_NS) ? SVG_NS.length : 0) - (root.includes(XLINK_NS) ? XLINK_NS.length : 0);
}

/**
 * A drawing, as one SVG, made safe to keep. It is only ever shown as a
 * picture, where nothing in it can run or load anything, and it is kept free
 * of anything that could all the same: scripts, event handlers, embedded HTML,
 * and links or CSS that reach outside the drawing. Cleaning only takes away,
 * and drawingLength leaves out what it adds, so what it keeps is within the
 * limit too, and cleans to itself.
 */
export function cleanDrawing(raw: unknown): string | undefined {
  if (typeof raw !== "string" || drawingLength(raw) > MAX_DRAWING) return undefined;
  const start = raw.search(/<svg[\s>]/i);
  const end = raw.toLowerCase().lastIndexOf("</svg>");
  if (start < 0 || end < start) return undefined;
  const svg = raw
    .slice(start, end + 6)
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<script\b[^>]*\/?>/gi, "")
    .replace(/<foreignObject\b[\s\S]*?<\/foreignObject\s*>/gi, "")
    .replace(/<foreignObject\b[^>]*\/?>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\s(?:xlink:)?href\s*=\s*("|')\s*(?!#)[^"']*\1/gi, "")
    .replace(/url\(\s*(['"]?)\s*(?!#)[^)'"]*\1\s*\)/gi, "none")
    .replace(/@import\b[^;]*;?/gi, "");
  // a browser shows an SVG as a picture only when it says it is one; a drawing written as it would be inside
  // a web page often leaves that out, and then nothing shows
  return svg.replace(/^<svg\b[^>]*>/i, (head) => {
    let h = head;
    if (!/\sxmlns\s*=/i.test(h)) h = h.replace(/^<svg/i, "<svg" + SVG_NS);
    if (/\bxlink:/i.test(svg) && !/\sxmlns:xlink\s*=/i.test(h)) h = h.replace(/^<svg/i, "<svg" + XLINK_NS);
    return h;
  });
}

/** Keep only what the scene engine understands. */
export function cleanScene(raw: unknown): SceneSpec {
  const s = isObj(raw) ? raw : {};
  const out: SceneSpec = {};
  if (s.style === "pixel" || s.style === "abstract") out.style = s.style;
  const seed = numOf(s.seed, 0, 1e9);
  if (seed !== undefined) out.seed = Math.round(seed);
  const sky = colorsOf(s.sky, 8);
  if (sky.length) out.sky = sky;
  const horizon = numOf(s.horizon, 0.35, 0.92);
  if (horizon !== undefined) out.horizon = horizon;
  if (isObj(s.body)) {
    const b = s.body;
    const kind = b.kind === "moon" || b.kind === "sun" || b.kind === "planet" || b.kind === "none" ? b.kind : undefined;
    out.body = {
      ...(kind ? { kind } : {}),
      ...(numOf(b.x, 0, 1) !== undefined ? { x: numOf(b.x, 0, 1) } : {}),
      ...(numOf(b.y, 0, 1) !== undefined ? { y: numOf(b.y, 0, 1) } : {}),
      ...(numOf(b.size, 0.02, 0.35) !== undefined ? { size: numOf(b.size, 0.02, 0.35) } : {}),
      ...(hexOf(b.color) ? { color: hexOf(b.color) } : {}),
      ...(b.glow === null || b.glow === false ? { glow: undefined } : hexOf(b.glow) ? { glow: hexOf(b.glow) } : {}),
      ...(hexOf(b.ring) ? { ring: hexOf(b.ring) } : {}),
    };
  } else if (s.body === null || s.body === false) out.body = { kind: "none" };
  if (isObj(s.stars)) out.stars = { ...(numOf(s.stars.density, 0, 1) !== undefined ? { density: numOf(s.stars.density, 0, 1) } : {}), ...(hexOf(s.stars.color) ? { color: hexOf(s.stars.color) } : {}) };
  if (isObj(s.aurora)) {
    const colors = colorsOf(s.aurora.colors, 4);
    if (colors.length) out.aurora = { colors, ...(numOf(s.aurora.strength, 0.1, 1) !== undefined ? { strength: numOf(s.aurora.strength, 0.1, 1) } : {}) };
  }
  if (isObj(s.clouds) && hexOf(s.clouds.color)) {
    const c = s.clouds;
    out.clouds = {
      color: hexOf(c.color)!,
      ...(hexOf(c.light) ? { light: hexOf(c.light) } : {}),
      ...(hexOf(c.shade) ? { shade: hexOf(c.shade) } : {}),
      ...(numOf(c.count, 0, 12) !== undefined ? { count: Math.round(numOf(c.count, 0, 12)!) } : {}),
      ...(numOf(c.at, 0.05, 0.8) !== undefined ? { at: numOf(c.at, 0.05, 0.8) } : {}),
      ...(numOf(c.speed, 0, 3) !== undefined ? { speed: numOf(c.speed, 0, 3) } : {}),
    };
  }
  if (Array.isArray(s.layers)) {
    const layers: SceneLayer[] = [];
    for (const l of s.layers.slice(0, 7)) {
      if (!isObj(l) || !LAYER_KINDS.includes(l.kind as SceneLayerKind) || !hexOf(l.color)) continue;
      const layer: SceneLayer = { kind: l.kind as SceneLayerKind, color: hexOf(l.color)! };
      if (l.light === null || l.light === false) layer.light = undefined;
      else if (hexOf(l.light)) layer.light = hexOf(l.light);
      const at = numOf(l.at, 0.2, 0.98);
      if (at !== undefined) layer.at = at;
      const height = numOf(l.height, 0, 0.5);
      if (height !== undefined) layer.height = height;
      const rough = numOf(l.rough, 0, 1);
      if (rough !== undefined) layer.rough = rough;
      const x = numOf(l.x, 0, 1);
      if (x !== undefined) layer.x = x;
      if (hexOf(l.snow)) layer.snow = hexOf(l.snow);
      if (hexOf(l.windows)) layer.windows = hexOf(l.windows);
      if (isObj(l.trees) && hexOf(l.trees.color)) {
        const t = l.trees;
        layer.trees = {
          color: hexOf(t.color)!,
          ...(t.kind === "pine" || t.kind === "round" || t.kind === "palm" || t.kind === "cherry" ? { kind: t.kind } : {}),
          ...(hexOf(t.light) ? { light: hexOf(t.light) } : {}),
          ...(numOf(t.density, 0, 1) !== undefined ? { density: numOf(t.density, 0, 1) } : {}),
        };
      }
      layers.push(layer);
    }
    if (layers.length) out.layers = layers;
  }
  if (isObj(s.water) && hexOf(s.water.color)) out.water = { color: hexOf(s.water.color)!, ...(hexOf(s.water.light) ? { light: hexOf(s.water.light) } : {}), ...(numOf(s.water.at, 0.3, 0.95) !== undefined ? { at: numOf(s.water.at, 0.3, 0.95) } : {}) };
  if (isObj(s.grid) && hexOf(s.grid.color)) out.grid = { color: hexOf(s.grid.color)!, ...(numOf(s.grid.at, 0.3, 0.95) !== undefined ? { at: numOf(s.grid.at, 0.3, 0.95) } : {}) };
  if (isObj(s.particles) && PARTICLE_KINDS.includes(s.particles.kind as ParticleKind))
    out.particles = { kind: s.particles.kind as ParticleKind, ...(hexOf(s.particles.color) ? { color: hexOf(s.particles.color) } : {}), ...(numOf(s.particles.density, 0, 1) !== undefined ? { density: numOf(s.particles.density, 0, 1) } : {}) };
  const flowers = colorsOf(s.flowers, 6);
  if (flowers.length) out.flowers = flowers;
  if (isObj(s.fog) && hexOf(s.fog.color)) out.fog = { color: hexOf(s.fog.color)!, ...(numOf(s.fog.at, 0.2, 0.98) !== undefined ? { at: numOf(s.fog.at, 0.2, 0.98) } : {}), ...(numOf(s.fog.amount, 0, 1) !== undefined ? { amount: numOf(s.fog.amount, 0, 1) } : {}) };
  if (Array.isArray(s.blobs)) {
    const blobs = s.blobs
      .filter(isObj)
      .slice(0, 6)
      .map((b) => ({ x: numOf(b.x, -0.2, 1.2) ?? 0.5, y: numOf(b.y, -0.2, 1.2) ?? 0.3, r: numOf(b.r, 0.05, 1.2) ?? 0.5, color: hexOf(b.color) ?? "" }))
      .filter((b) => b.color);
    if (blobs.length) out.blobs = blobs;
  }
  const grain = numOf(s.grain, 0, 1);
  if (grain !== undefined) out.grain = grain;
  const vignette = numOf(s.vignette, 0, 1);
  if (vignette !== undefined) out.vignette = vignette;
  return out;
}

/** Pictures the board already keeps, by their keys: links are fetched and turned into these before a theme is cleaned. */
export function cleanBackgrounds(raw: unknown): ThemeBackground[] {
  if (!Array.isArray(raw)) return [];
  const out: ThemeBackground[] = [];
  for (const b of raw) {
    // a picture the board keeps, or one from its own library
    if (!isObj(b) || typeof b.key !== "string" || !(IMAGE_KEY.test(b.key) || libraryOf(b.key)) || out.some((x) => x.key === b.key)) continue;
    const w = numOf(b.w, 1, 30000);
    const h = numOf(b.h, 1, 30000);
    if (!w || !h) continue;
    const tone = hexOf(b.tone);
    const from = typeof b.from === "string" && /^https?:\/\/[^\s<>"']+$/i.test(b.from) ? b.from.slice(0, 300) : undefined;
    out.push({ key: b.key, w: Math.round(w), h: Math.round(h), ...(tone ? { tone } : {}), ...(from ? { from } : {}) });
    if (out.length >= 6) break;
  }
  return out;
}

function cleanColors(raw: unknown): ThemeColors | string {
  const c = isObj(raw) ? raw : {};
  const bg = hexOf(c.bg);
  const tile = hexOf(c.tile);
  const ink = hexOf(c.ink ?? c.text ?? c.fg);
  const accent = hexOf(c.accent);
  const missing = [!bg && "bg", !tile && "tile", !ink && "ink", !accent && "accent"].filter(Boolean);
  if (missing.length) return `colors needs ${missing.join(", ")} as #hex colors.`;
  const out: ThemeColors = { bg: bg!, tile: tile!, ink: ink!, accent: accent! };
  for (const k of ["ink2", "ink3", "ok", "warn", "bad", "border"] as const) {
    const v = hexOf(c[k]);
    if (v) out[k] = v;
  }
  const charts = colorsOf(c.charts, 5);
  if (charts.length) out.charts = charts;
  return out;
}

/** A theme from anywhere, made safe to keep: the theme, or what is wrong with it in plain words. */
export function cleanTheme(raw: unknown, meta: { id: string; by: string | null; now: number; createdAt?: number }): CustomTheme | string {
  const t = isObj(raw) ? raw : {};
  const name = textOf(t.name, 40);
  if (!name) return "A theme needs a name.";
  const backgrounds = cleanBackgrounds(t.backgrounds);
  const drawing = cleanDrawing(t.drawing);
  // with pictures, or a drawing the board will turn into one, colors can wait for the board to take them
  // from the first. A picture from the board's own library was measured when it joined the library, so
  // its colors are there at once
  const leftToPictures = (backgrounds.length > 0 || !!drawing) && (t.colors === "auto" || t.colors === undefined || t.colors === null);
  const lib = leftToPictures && backgrounds.length ? libraryOf(backgrounds[0].key) : null;
  const auto = leftToPictures && !lib;
  const colors = lib ? { ...lib.colors, ...(lib.colors.charts ? { charts: [...lib.colors.charts] } : {}) } : auto ? { bg: "#0e1014", tile: "#14171d", ink: "#f2f4f7", accent: "#c8d2e2" } : cleanColors(t.colors);
  if (typeof colors === "string") return colors;
  // colors from a picture bring its light or dark with them, as the board's own measuring does
  const mode = lib ? lib.tone : t.mode === "light" ? "light" : t.mode === "dark" ? "dark" : undefined;
  const glass = numOf(t.glass, 0, 1) ?? (lib ? 0.22 : undefined);
  const blur = numOf(t.blur, 0, 32) ?? (lib ? 18 : undefined);
  const radius = numOf(t.radius, 0, 28);
  const effects = cleanEffects(t.effects);
  return {
    id: meta.id,
    name,
    blurb: textOf(t.blurb, 120),
    by: meta.by,
    mode: mode ?? (lightish(colors.tile) ? "light" : "dark"),
    font: FONTS.includes(t.font as FontVoice) ? (t.font as FontVoice) : "clean",
    colors,
    ...(glass !== undefined ? { glass } : {}),
    ...(blur !== undefined ? { blur: Math.round(blur) } : {}),
    ...(radius !== undefined ? { radius: Math.round(radius) } : {}),
    scene: cleanScene(t.scene),
    ...(backgrounds.length ? { backgrounds } : {}),
    // an empty list is a choice too: none, even where the scene asks for falling things
    ...(effects.length || Array.isArray(t.effects) ? { effects } : {}),
    ...(drawing ? { drawing } : {}),
    ...(t.pixel === true ? { pixel: true } : {}),
    ...(auto ? { auto: true } : {}),
    createdAt: meta.createdAt ?? meta.now,
    updatedAt: meta.now,
  };
}

function lightish(c: string): boolean {
  const n = parseInt(c.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150;
}
